import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { X, Cookie, Settings, Shield } from 'lucide-react';

const CONSENT_KEY = 'cookie_consent';

const getConsent = () => {
  try {
    // Cookie consent is non-sensitive preference data — safe for localStorage
    const stored = localStorage.getItem(CONSENT_KEY); // eslint-disable-line no-restricted-globals
    return stored ? JSON.parse(stored) : null;
  } catch { return null; }
};

const setConsent = (consent) => {
  localStorage.setItem(CONSENT_KEY, JSON.stringify({ ...consent, timestamp: new Date().toISOString() })); // eslint-disable-line no-restricted-globals
  // Dispatch event so other components can react
  window.dispatchEvent(new CustomEvent('cookie-consent-change', { detail: consent }));
};

export const hasAnalyticsConsent = () => {
  const consent = getConsent();
  return consent?.analytics === true;
};

export const hasFunctionalConsent = () => {
  const consent = getConsent();
  return consent?.functional !== false; // Default true if not explicitly rejected
};

const CookieConsentBanner = () => {
  const [visible, setVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always on
    analytics: true,
    functional: true,
  });

  useEffect(() => {
    const consent = getConsent();
    if (!consent) {
      // Show banner after 2 seconds
      const timer = setTimeout(() => setVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = { essential: true, analytics: true, functional: true };
    setConsent(consent);
    setVisible(false);
    enableAnalytics();
  };

  const handleRejectNonEssential = () => {
    const consent = { essential: true, analytics: false, functional: false };
    setConsent(consent);
    setVisible(false);
    disableAnalytics();
  };

  const handleSavePreferences = () => {
    setConsent(preferences);
    setVisible(false);
    if (preferences.analytics) enableAnalytics();
    else disableAnalytics();
  };

  const enableAnalytics = () => {
    // Enable Microsoft Clarity if it was blocked
    if (window.clarity) window.clarity('consent');
  };

  const disableAnalytics = () => {
    // Disable Clarity tracking
    if (window.clarity) window.clarity('stop');
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9999] p-4 animate-in slide-in-from-bottom" data-testid="cookie-consent-banner">
      <div className="max-w-4xl mx-auto bg-neutral-900 border border-neutral-700 rounded-xl shadow-2xl overflow-hidden">
        {!showPreferences ? (
          /* Main Banner */
          <div className="p-5">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <h3 className="font-semibold text-white mb-1">We value your privacy</h3>
                <p className="text-sm text-neutral-400">
                  We use cookies to enhance your experience, analyze site traffic, and understand visitor behavior. 
                  Essential cookies are always active. You can choose to accept or reject analytics cookies.{' '}
                  <Link to="/cookies" className="text-amber-500 hover:underline">Cookie Policy</Link> |{' '}
                  <Link to="/privacy-policy" className="text-amber-500 hover:underline">Privacy Policy</Link>
                </p>
              </div>
              <button onClick={() => setVisible(false)} className="text-neutral-400 hover:text-white flex-shrink-0" aria-label="Close cookie consent banner">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-4 ml-14">
              <Button onClick={handleAcceptAll} className="bg-amber-500 hover:bg-amber-600 text-black" data-testid="cookie-accept-all">
                Accept All
              </Button>
              <Button onClick={handleRejectNonEssential} variant="outline" className="border-neutral-500 text-white hover:bg-neutral-800 hover:text-white" data-testid="cookie-reject">
                Reject Non-Essential
              </Button>
              <Button onClick={() => setShowPreferences(true)} variant="ghost" className="text-neutral-400 hover:text-white" data-testid="cookie-manage">
                <Settings className="w-4 h-4 mr-2" />Manage Preferences
              </Button>
            </div>
          </div>
        ) : (
          /* Preferences Panel */
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Cookie Preferences
              </h3>
              <button onClick={() => setShowPreferences(false)} className="text-neutral-400 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="space-y-4">
              {/* Essential */}
              <div className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Essential Cookies</p>
                  <p className="text-xs text-neutral-400">Required for login, security, and basic functionality</p>
                </div>
                <Switch checked={true} disabled className="opacity-60" />
              </div>

              {/* Analytics */}
              <div className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Analytics Cookies</p>
                  <p className="text-xs text-neutral-400">Microsoft Clarity heatmaps, anonymous visitor tracking, geographic analytics</p>
                </div>
                <Switch checked={preferences.analytics} onCheckedChange={(v) => setPreferences({ ...preferences, analytics: v })} />
              </div>

              {/* Functional */}
              <div className="flex items-center justify-between p-3 bg-neutral-800 rounded-lg">
                <div>
                  <p className="font-medium text-white text-sm">Functional Cookies</p>
                  <p className="text-xs text-neutral-400">GPS location (with permission), company filter memory, enhanced features</p>
                </div>
                <Switch checked={preferences.functional} onCheckedChange={(v) => setPreferences({ ...preferences, functional: v })} />
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <Button onClick={handleSavePreferences} className="flex-1 bg-amber-500 hover:bg-amber-600 text-black" data-testid="cookie-save-prefs">
                Save Preferences
              </Button>
              <Button onClick={handleAcceptAll} variant="outline" className="flex-1 border-neutral-500 text-white hover:bg-neutral-800 hover:text-white">
                Accept All
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsentBanner;
